/**
 * Shared mapping from a `getUserMedia` rejection to a user-facing message
 * with actionable advice. Used by `useWebRTCMedia` for the in-call stream
 * and by `useMediaDevices` for the initial permission probe, so the
 * messaging stays consistent wherever a media error can surface.
 */

export interface MediaErrorInfo {
  /**
   * The DOMException name, e.g. 'NotReadableError'. Preserved so callers
   * can branch on specific error classes when needed.
   */
  name: string;
  /**
   * Short user-facing heading, suitable for a banner title or toast header.
   */
  title: string;
  /**
   * One-sentence explanation of what went wrong, in plain language.
   */
  message: string;
  /**
   * Bullet list of things the user can try to fix the problem.
   */
  advice: string[];
  /**
   * Whether offering a retry button makes sense for this error class.
   */
  recoverable: boolean;
}

const NOT_READABLE_ADVICE = [
  'Close any other app that might be using the camera or microphone (Zoom, Teams, FaceTime, Photo Booth, OBS, etc.).',
  'Close other browser tabs or windows that have used the camera recently.',
  'If you use an external webcam, unplug it and plug it back in.',
  'Check that the physical camera shutter or privacy cover is open.',
  'If the problem persists, restart your browser.',
];

const NOT_ALLOWED_ADVICE = [
  'Click the camera icon in your browser address bar and allow camera and microphone access.',
  'On macOS, open System Settings → Privacy & Security → Camera and make sure your browser is enabled.',
  'On Windows, open Settings → Privacy & Security → Camera and make sure camera access is on.',
  'Once permissions are granted, click Retry.',
];

const NOT_FOUND_ADVICE = [
  'Connect a camera and a microphone to your computer.',
  'If they are already connected, check that the operating system recognises them.',
  'Click Retry once the devices are available.',
];

const OVERCONSTRAINED_ADVICE = [
  'Pick a different camera or microphone from the device selector below.',
  'If only one device is listed, try reconnecting it and clicking Retry.',
];

const ABORT_ADVICE = [
  'Click Retry to try again.',
  'If this keeps happening, reload the page.',
];

const DEFAULT_ADVICE = [
  'Click Retry to try again.',
  'If the problem continues, reload the page or restart your browser.',
];

export function getUserMediaErrorInfo(error: unknown): MediaErrorInfo {
  const name = error instanceof Error ? error.name : 'UnknownError';
  const rawMessage =
    error instanceof Error && error.message ? error.message : '';

  switch (name) {
    case 'NotReadableError':
    case 'TrackStartError':
      return {
        name,
        title: 'Camera or microphone is unavailable',
        message:
          'Your camera or microphone could not be started. It is most likely being used by another application.',
        advice: NOT_READABLE_ADVICE,
        recoverable: true,
      };

    case 'NotAllowedError':
    case 'SecurityError':
      return {
        name,
        title: 'Camera and microphone access denied',
        message:
          'This site needs permission to use your camera and microphone to place a call.',
        advice: NOT_ALLOWED_ADVICE,
        recoverable: true,
      };

    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return {
        name,
        title: 'No camera or microphone found',
        message:
          'We could not find a camera or microphone connected to this device.',
        advice: NOT_FOUND_ADVICE,
        recoverable: true,
      };

    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return {
        name,
        title: "Selected device doesn't support the required settings",
        message:
          'The camera or microphone you selected cannot be started with the requested settings.',
        advice: OVERCONSTRAINED_ADVICE,
        recoverable: true,
      };

    case 'AbortError':
      return {
        name,
        title: 'Camera start was interrupted',
        message: 'Starting the camera was interrupted before it could finish.',
        advice: ABORT_ADVICE,
        recoverable: true,
      };

    default:
      return {
        name,
        title: 'Could not access camera or microphone',
        message: rawMessage
          ? `An unexpected error occurred: ${rawMessage}`
          : 'An unexpected error occurred while accessing your camera or microphone.',
        advice: DEFAULT_ADVICE,
        recoverable: true,
      };
  }
}
